import {
  FiscalYearMonth,
  formatDateTimeToLocaleString,
  formatDateToMediumDateTime,
  getFiscalYearStartMonthNumber,
  IsoLocale,
  Locales,
} from "@simplyk/common";
import { DateTime } from "luxon";

export const formatDateOrIdem = (dateString: string, locale: Locales): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }

  return formatDateToMediumDateTime(date, locale);
};

export interface TransactionDateFilter {
  month: number;
  year: number;
}

export const getFormattedMonthAndYear = ({
  locale,
  dateObject,
}: {
  dateObject: TransactionDateFilter;
  locale: Locales;
}) => {
  const formattedMonth = formatDateTimeToLocaleString(new Date(dateObject.year, dateObject.month, 1), locale, {
    month: "long",
  });
  return `${formattedMonth} ${dateObject.year}`;
};

export const formatYearCycleMonths = (
  fiscalYearMonthStart: FiscalYearMonth,
  locale: Locales,
  month: "long" | "short"
): { start: string; end: string } => {
  const startMonth = getFiscalYearStartMonthNumber(fiscalYearMonthStart);
  const endMonth = ((startMonth + 10) % 12) + 1;
  const formatMonth = (monthNumber: number) =>
    formatDateTimeToLocaleString(new Date(2024, monthNumber - 1, 1), locale, { month });

  return { start: formatMonth(startMonth), end: formatMonth(endMonth) };
};

/** Day + abbreviated month, no year, localized: "May 20" / "20 mai". */
export const formatDayAndMonth = (date: Date, locale: IsoLocale): string =>
  DateTime.fromJSDate(date).setLocale(locale).toLocaleString({ month: "short", day: "numeric" });

export const formatRelativeDate = (
  date: Date,
  { locale, now, justNowLabel }: { locale: IsoLocale; now?: Date; justNowLabel: string }
) => {
  const luxonNow = now ? DateTime.fromJSDate(now) : DateTime.now();

  if (Math.abs(DateTime.fromJSDate(date).diff(luxonNow).as("seconds")) < 60) {
    // When less than a minute, show "just now"
    return justNowLabel;
  }

  const relativeDate = DateTime.fromJSDate(date).toRelative({ locale, base: luxonNow });
  return relativeDate ?? "";
};

/** Compares a UTC due-date instant to today using the viewer's local calendar day. */
export const isUtcDueDateBeforeLocalToday = (dueDateAtUtc: string): boolean => {
  const dueDayLocal = DateTime.fromISO(dueDateAtUtc, { zone: "utc" }).setZone("local").startOf("day");
  if (!dueDayLocal.isValid) {
    return false;
  }
  return dueDayLocal < DateTime.local().startOf("day");
};

export const formatRelativeDateDayLevel = (
  date: Date,
  { locale, todayLabel, now }: { locale: IsoLocale; todayLabel: string; now?: Date }
) => {
  const luxonDate = DateTime.fromJSDate(date).startOf("day");
  const luxonToday = (now ? DateTime.fromJSDate(now) : DateTime.now()).startOf("day");
  const diffDays = Math.round(luxonDate.diff(luxonToday, "days").days);

  if (diffDays === 0) {
    return todayLabel;
  }

  if (Math.abs(diffDays) < 7) {
    return luxonDate.toRelative({ locale, unit: "days", base: luxonToday }) ?? "";
  }

  if (Math.abs(diffDays) < 30) {
    return luxonDate.toRelative({ locale, unit: "weeks", base: luxonToday }) ?? "";
  }

  const years = Math.trunc(luxonDate.diff(luxonToday, "years").years);

  if (Math.abs(years) === 0) {
    return luxonDate.toRelative({ locale, unit: "months", base: luxonToday }) ?? "";
  }

  return luxonDate.toRelative({ locale, unit: "years", base: luxonToday }) ?? "";
};
